import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * ZAKŁADKI — najmniejszy możliwy prymityw, bez biblioteki.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ STAN TRZYMA WOŁAJĄCY, nie ten komponent. Karta automatu ma zakładkę w adresie URL, żeby
 *  „wyślij komuś link do modułów tej maszyny" znaczyło to samo po odświeżeniu strony. Komponent
 *  ze własnym `useState` uniemożliwiałby to bez obejść.
 *
 *  ⚠️ ROLE ARIA SĄ TU TREŚCIĄ, NIE OZDOBĄ: `role="tablist"`/`role="tab"` z `aria-selected`
 *  to jedyna rzecz, po której czytnik ekranu wie, że to przełącznik widoku, a nie zestaw
 *  przycisków. Strzałki w lewo/prawo obsługujemy sami, bo tego oczekuje wzorzec zakładek.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export type Zakladka<T extends string> = {
  id: T;
  etykieta: string;
  /** Liczba obok etykiety — np. ile bębnów. `null` = nie pokazuj. */
  licznik?: number | null;
  ikona?: ReactNode;
};

export function Tabs<T extends string>({
  zakladki,
  wybrana,
  onWybor,
  className,
}: {
  zakladki: readonly Zakladka<T>[];
  wybrana: T;
  onWybor: (id: T) => void;
  className?: string;
}) {
  const naKlawisz = (e: React.KeyboardEvent, i: number): void => {
    const krok = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (krok === 0) return;
    e.preventDefault();
    /** Zawijamy na końcach — inaczej ostatnia strzałka w prawo nie robi nic i wygląda na zepsutą. */
    const nastepna = zakladki[(i + krok + zakladki.length) % zakladki.length];
    if (nastepna !== undefined) onWybor(nastepna.id);
  };

  return (
    <div role="tablist" className={cn('flex items-center gap-1 border-b', className)}>
      {zakladki.map((z, i) => {
        const aktywna = z.id === wybrana;
        return (
          <button
            key={z.id}
            type="button"
            role="tab"
            aria-selected={aktywna}
            /**
             * ⚠️ `-mb-px` I OBRAMOWANIE DOLNE W KOLORZE TŁA: zakładka aktywna musi PRZERWAĆ
             * linię pojemnika, inaczej wygląda jak przycisk pod linią, a nie jak karta na wierzchu.
             */
            className={cn(
              '-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              aktywna
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
            onClick={() => onWybor(z.id)}
            onKeyDown={(e) => naKlawisz(e, i)}
          >
            {z.ikona}
            {z.etykieta}
            {z.licznik !== null && z.licznik !== undefined && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{z.licznik}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
